import { SubcategoryAllocation } from '../types/zoneMapType';

/** A single color stop for a boundary gradient. */
export interface GradientColorWeight {
  color: string;
  weight: number;
}

export interface FixtureAssignment {
  fixtureId: string;
  sequenceOrder: number;
  subcategoryId: string;
  subcategoryName: string;
  color: string;
  isBoundary: boolean;
  /** Gradient stops ordered to match the fixture's visual direction (position 0 → position 1 along local X). */
  boundaryGradient?: GradientColorWeight[];
  /** Subcategory names matching boundaryGradient order (visual left-to-right along the fixture). */
  boundaryNames?: string[];
}

export interface FixtureSequenceEntry {
  fixtureId: string;
  sequenceOrder: number;
}

/** Position and rotation data for a fixture, used to compute gradient direction. */
export interface FixturePositionData {
  translation: number[]; // [x, y, ...] world position of the fixture pivot
  rotation: number; // clockwise degrees around Z axis
  width: number; // fixture width along local X axis
}

/**
 * Computes the approximate world-space center (X/Y) of a fixture.
 *
 * The pivot is at the back bottom-left corner. We offset by half the width
 * along the fixture's local X axis to get the center. The depth offset along
 * local Y is omitted because it's perpendicular to the gradient axis and
 * doesn't affect the direction dot product.
 *
 * Coordinate note: the renderer negates Y (worldY = -dataY) and applies
 * rotation as -rotRad, so local +X projected back to data coords is
 * [cos(rotRad), sin(rotRad)].
 */
export function fixtureCenter(pos: FixturePositionData): [number, number] {
  const rotRad = (pos.rotation * Math.PI) / 180;
  const halfW = pos.width / 2;
  return [pos.translation[0] + halfW * Math.cos(rotRad), pos.translation[1] + halfW * Math.sin(rotRad)];
}

/**
 * Determines whether the gradient should be flipped for a boundary fixture.
 *
 * The gradient texture maps left-to-right along the fixture's local X axis
 * (position 0 = left edge, position 1 = right edge). We want the "earlier"
 * subcategory to appear on the side facing the previous fixtures in the sequence.
 *
 * We compute the sequence direction (prev → next neighbor centers), then dot it
 * with the fixture's local X axis in data coordinates. A negative dot product
 * means the sequence flows against local X, so the gradient must be flipped.
 *
 * @returns true if the gradient colors should be reversed
 */
export function shouldFlipGradient(
  prevCenter: [number, number] | null,
  currCenter: [number, number] | null,
  nextCenter: [number, number] | null,
  fixtureRotationDeg: number,
): boolean {
  // Compute sequence direction vector from available neighbors
  let seqDirX = 0;
  let seqDirY = 0;

  if (prevCenter && nextCenter) {
    seqDirX = nextCenter[0] - prevCenter[0];
    seqDirY = nextCenter[1] - prevCenter[1];
  } else if (nextCenter && currCenter) {
    seqDirX = nextCenter[0] - currCenter[0];
    seqDirY = nextCenter[1] - currCenter[1];
  } else if (prevCenter && currCenter) {
    seqDirX = currCenter[0] - prevCenter[0];
    seqDirY = currCenter[1] - prevCenter[1];
  } else {
    return false; // no neighbors available, can't determine direction
  }

  const len = Math.sqrt(seqDirX * seqDirX + seqDirY * seqDirY);
  if (len < 1e-6) return false; // coincident fixtures, can't determine direction

  seqDirX /= len;
  seqDirY /= len;

  // Fixture's local X axis in data coordinates.
  // The renderer negates Y (worldY = -dataY) and applies rotation as -rotRad,
  // so the local +X in renderer world space is [cos(rotRad), -sin(rotRad)].
  // Projecting back to data coords (flip Y) gives [cos(rotRad), sin(rotRad)].
  const rotRad = (fixtureRotationDeg * Math.PI) / 180;
  const localXx = Math.cos(rotRad);
  const localXy = Math.sin(rotRad);

  return seqDirX * localXx + seqDirY * localXy < 0;
}

export function computeFixtureAllocations(
  allocations: SubcategoryAllocation[],
  fixtureSequence: FixtureSequenceEntry[],
  fixturePositions?: Map<string, FixturePositionData>,
): FixtureAssignment[] {
  if (fixtureSequence.length === 0 || allocations.length === 0) {
    return [];
  }

  const sorted = [...fixtureSequence].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
  const totalFixtures = sorted.length;

  // Build cumulative breakpoints from allocations
  // e.g. [10%, 20%, 70%] → breakpoints [10, 30, 100]
  const breakpoints: number[] = [];
  let cumulative = 0;
  for (const alloc of allocations) {
    cumulative += alloc.percentage;
    breakpoints.push(cumulative);
  }

  // Pre-compute fixture centers for gradient direction calculation
  const centerCache = new Map<string, [number, number]>();
  if (fixturePositions) {
    for (const [id, pos] of fixturePositions) {
      centerCache.set(id, fixtureCenter(pos));
    }
  }

  const assignments: FixtureAssignment[] = [];
  const totalAllocated = breakpoints[breakpoints.length - 1];

  for (let i = 0; i < totalFixtures; i++) {
    const fixture = sorted[i];
    const position = (i / totalFixtures) * 100;
    const endPosition = ((i + 1) / totalFixtures) * 100;

    // Skip fixtures beyond the total allocated percentage
    if (position >= totalAllocated) {
      continue;
    }

    const startIdx = findAllocationIndex(breakpoints, position);
    const endIdx = findAllocationIndex(breakpoints, endPosition);
    const effectiveEndIdx = endPosition <= breakpoints[startIdx] ? startIdx : endIdx;

    // Fixture spans beyond allocated range — treat as solid
    if (endPosition > totalAllocated && startIdx === breakpoints.length - 1) {
      assignments.push({
        fixtureId: fixture.fixtureId,
        sequenceOrder: fixture.sequenceOrder,
        subcategoryId: allocations[startIdx].subcategoryId,
        subcategoryName: allocations[startIdx].subcategoryName,
        color: allocations[startIdx].color,
        isBoundary: false,
      });
      continue;
    }

    if (startIdx === effectiveEndIdx) {
      // Solid assignment — entire fixture falls within one subcategory
      assignments.push({
        fixtureId: fixture.fixtureId,
        sequenceOrder: fixture.sequenceOrder,
        subcategoryId: allocations[startIdx].subcategoryId,
        subcategoryName: allocations[startIdx].subcategoryName,
        color: allocations[startIdx].color,
        isBoundary: false,
      });
    } else {
      // Boundary fixture — spans a breakpoint between two subcategories
      const breakpoint = breakpoints[startIdx];
      const firstWeight = (breakpoint - position) / (endPosition - position);
      const secondWeight = 1 - firstWeight;
      const primaryIdx = firstWeight >= secondWeight ? startIdx : effectiveEndIdx;

      // Default gradient order: earlier subcategory at position 0, later at position 1
      let gradientFirst: GradientColorWeight = { color: allocations[startIdx].color, weight: firstWeight };
      let gradientSecond: GradientColorWeight = { color: allocations[effectiveEndIdx].color, weight: secondWeight };
      let nameOrder = [allocations[startIdx].subcategoryName, allocations[effectiveEndIdx].subcategoryName];

      // Check if gradient needs flipping based on fixture rotation vs sequence direction
      if (fixturePositions) {
        const fixturePos = fixturePositions.get(fixture.fixtureId);
        if (fixturePos) {
          const prevCenter = i > 0 ? (centerCache.get(sorted[i - 1].fixtureId) ?? null) : null;
          const currCenter = centerCache.get(fixture.fixtureId) ?? null;
          const nextCenter = i < totalFixtures - 1 ? (centerCache.get(sorted[i + 1].fixtureId) ?? null) : null;

          if (shouldFlipGradient(prevCenter, currCenter, nextCenter, fixturePos.rotation)) {
            gradientFirst = { color: allocations[effectiveEndIdx].color, weight: secondWeight };
            gradientSecond = { color: allocations[startIdx].color, weight: firstWeight };
            nameOrder = [allocations[effectiveEndIdx].subcategoryName, allocations[startIdx].subcategoryName];
          }
        }
      }

      assignments.push({
        fixtureId: fixture.fixtureId,
        sequenceOrder: fixture.sequenceOrder,
        subcategoryId: allocations[primaryIdx].subcategoryId,
        subcategoryName: allocations[primaryIdx].subcategoryName,
        color: allocations[primaryIdx].color,
        isBoundary: true,
        boundaryGradient: [gradientFirst, gradientSecond],
        boundaryNames: nameOrder,
      });
    }
  }

  return assignments;
}

/**
 * Find the index of the allocation that owns the given position.
 * Breakpoints are cumulative percentages, e.g. [10, 30, 100].
 * Position 0 → index 0, position 10 → index 1, position 99 → index 2.
 */
function findAllocationIndex(breakpoints: number[], position: number): number {
  for (let i = 0; i < breakpoints.length; i++) {
    if (position < breakpoints[i]) {
      return i;
    }
  }
  return breakpoints.length - 1;
}
